import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { Button } from 'reactstrap';
import { Drawer, DrawerControls } from '../../../core/components/Drawer';
import { getGroupDisplayName, getOccurrenceType, getTaskBucket, TASK_BUCKET } from '../helpers';
import { TemplateType, TemplateTypeLabel } from '../models';

type DrawerMode =
  | 'add'
  | 'duplicate'
  | 'move'
  | 'addGroup'
  | 'addAsset'
  | 'editTask'
  | 'editGroup'
  | 'editAsset'
  | 'editTemplate';
type AddItemType = 'dailyTask' | 'prevMaint' | 'project' | 'inventory';

interface AddToNodeDrawerProps {
  isOpen: boolean;
  mode: DrawerMode | null;
  node: any;
  breadcrumb: any[];
  groups?: any[];
  taskOccurrences?: any[];
  inventoryItems?: any[];
  isAssetContext?: boolean;
  initialItemType?: AddItemType | null;
  editData?: any;
  onClose: () => void;
  onSubmit: (mode: DrawerMode, itemType: AddItemType | null, data: any) => void;
}

type LinkTaskPayload = { linkOccurrenceId: number; __link: true };
type LinkInventoryPayload = { linkInventoryId: number; __linkInventory: true };
type OpenTaskEditorPayload = { __openTaskEditor: true };
type OpenInventoryEditorPayload = { __openInventoryEditor: true };

const NODE_DRAWER_WIDTH = 40;

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

const typeCards: { key: AddItemType; icon: string; colorClass: string }[] = [
  { key: 'dailyTask', icon: 'uil-clipboard-notes', colorClass: 'blue' },
  { key: 'prevMaint', icon: 'uil-wrench', colorClass: 'orange' },
  { key: 'project', icon: 'uil-folder', colorClass: 'purple' },
  { key: 'inventory', icon: 'uil-box', colorClass: 'green' },
];

const formatOccurrenceLabel = (occurrence: any): string => {
  const name = occurrence?.task?.name || occurrence?.name || `Task #${occurrence?.id}`;
  const interval = occurrence?.task?.interval ? ` \u00b7 ${occurrence.task.interval}` : '';
  return `${name}${interval}`;
};

const LinkExistingTaskForm: React.FC<{
  intl: any;
  nodeName: string;
  occurrences: any[];
  onSubmit: (data: LinkTaskPayload) => void;
  onCancel: () => void;
}> = ({ intl, nodeName, occurrences, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ linkOccurrenceId: string }>({
    defaultValues: { linkOccurrenceId: '' },
  });
  const hasOccurrences = Array.isArray(occurrences) && occurrences.length > 0;
  const handleValidSubmit = (values: { linkOccurrenceId: string }) => {
    const id = Number(values.linkOccurrenceId);
    if (!Number.isFinite(id) || id <= 0) return;
    onSubmit({ linkOccurrenceId: id, __link: true });
  };
  return (
    <form className="node-drawer__form" onSubmit={handleSubmit(handleValidSubmit)}>
      <p className="node-drawer__hint">
        {intl.formatMessage({ id: 'assets.drawer.linkTask.description' }, { nodeName })}
      </p>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.linkTask.title' })}
          <span className="node-drawer__required"> *</span>
        </label>
        {hasOccurrences ? (
          <select
            className={`node-drawer__input ${
              errors.linkOccurrenceId ? 'node-drawer__input--error' : ''
            }`}
            {...register('linkOccurrenceId', {
              required: intl.formatMessage({ id: 'assets.drawer.linkTask.required' }),
            })}
          >
            <option value="">
              {intl.formatMessage({ id: 'assets.drawer.linkTask.placeholder' })}
            </option>
            {occurrences.map((occurrence: any) => (
              <option key={occurrence.id} value={occurrence.id}>
                {formatOccurrenceLabel(occurrence)}
              </option>
            ))}
          </select>
        ) : (
          <div className="node-drawer__empty">
            {intl.formatMessage({ id: 'assets.drawer.linkTask.empty' })}
          </div>
        )}
        {errors.linkOccurrenceId && (
          <span className="node-drawer__error">{errors.linkOccurrenceId.message}</span>
        )}
      </div>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary" disabled={!hasOccurrences}>
          {intl.formatMessage({ id: 'assets.drawer.linkTask.submit' })}
        </Button>
      </div>
    </form>
  );
};

const CreateNewTaskCta: React.FC<{
  intl: any;
  nodeName: string;
  onSubmit: (data: OpenTaskEditorPayload) => void;
  onCancel: () => void;
}> = ({ intl, nodeName, onSubmit, onCancel }) => {
  return (
    <div className="node-drawer__form">
      <p className="node-drawer__hint">
        {intl.formatMessage({ id: 'assets.drawer.createTask.description' }, { nodeName })}
      </p>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="button" color="primary" onClick={() => onSubmit({ __openTaskEditor: true })}>
          {intl.formatMessage({ id: 'assets.drawer.createTask.open' })}
        </Button>
      </div>
    </div>
  );
};

const formatInventoryLabel = (inventory: any): string => {
  const name = inventory?.name || `Inventory #${inventory?.id}`;
  const number = inventory?.number ? ` \u00b7 ${inventory.number}` : '';
  return `${name}${number}`;
};

const CreateNewInventoryCta: React.FC<{
  intl: any;
  nodeName: string;
  onSubmit: (data: OpenInventoryEditorPayload) => void;
  onCancel: () => void;
}> = ({ intl, nodeName, onSubmit, onCancel }) => {
  return (
    <div className="node-drawer__form">
      <p className="node-drawer__hint">
        {intl.formatMessage({ id: 'assets.drawer.createInventory.description' }, { nodeName })}
      </p>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button
          type="button"
          color="primary"
          onClick={() => onSubmit({ __openInventoryEditor: true })}
        >
          {intl.formatMessage({ id: 'assets.drawer.createInventory.open' })}
        </Button>
      </div>
    </div>
  );
};

const LinkExistingInventoryForm: React.FC<{
  intl: any;
  nodeName: string;
  inventories: any[];
  onSubmit: (data: LinkInventoryPayload) => void;
  onCancel: () => void;
}> = ({ intl, nodeName, inventories, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ linkInventoryId: string }>({
    defaultValues: { linkInventoryId: '' },
  });
  const hasInventories = Array.isArray(inventories) && inventories.length > 0;
  const handleValidSubmit = (values: { linkInventoryId: string }) => {
    const id = Number(values.linkInventoryId);
    if (!Number.isFinite(id) || id <= 0) return;
    onSubmit({ linkInventoryId: id, __linkInventory: true });
  };
  return (
    <form className="node-drawer__form" onSubmit={handleSubmit(handleValidSubmit)}>
      <p className="node-drawer__hint">
        {intl.formatMessage({ id: 'assets.drawer.linkInventory.description' }, { nodeName })}
      </p>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.linkInventory.title' })}
          <span className="node-drawer__required"> *</span>
        </label>
        {hasInventories ? (
          <select
            className={`node-drawer__input ${
              errors.linkInventoryId ? 'node-drawer__input--error' : ''
            }`}
            {...register('linkInventoryId', {
              required: intl.formatMessage({ id: 'assets.drawer.linkInventory.required' }),
            })}
          >
            <option value="">
              {intl.formatMessage({ id: 'assets.drawer.linkInventory.placeholder' })}
            </option>
            {inventories.map((inv: any) => (
              <option key={inv.id} value={inv.id}>
                {formatInventoryLabel(inv)}
              </option>
            ))}
          </select>
        ) : (
          <div className="node-drawer__empty">
            {intl.formatMessage({ id: 'assets.drawer.linkInventory.empty' })}
          </div>
        )}
        {errors.linkInventoryId && (
          <span className="node-drawer__error">{errors.linkInventoryId.message}</span>
        )}
      </div>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary" disabled={!hasInventories}>
          {intl.formatMessage({ id: 'assets.drawer.linkInventory.submit' })}
        </Button>
      </div>
    </form>
  );
};

const EditTaskForm: React.FC<{
  intl: any;
  editData: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, editData, onSubmit, onCancel }) => {
  const raw = editData?.raw || editData || {};
  const taskType = (raw.type || '').toLowerCase();
  const isDaily = taskType === 'daily' || !taskType;
  const isPm = taskType === 'preventive_maintenance';
  const isProject = taskType === 'project';

  const priorityLabel =
    raw.priority === 4
      ? 'Critical'
      : raw.priority === 3
      ? 'High'
      : raw.priority === 1
      ? 'Low'
      : 'Medium';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: raw.name || editData?.name || '',
      status: raw.status != null ? String(raw.status) : '0',
      priority: isPm ? priorityLabel : 'Medium',
      description: raw.instructions || raw.description || '',
    },
  });

  return (
    <form className="node-drawer__form" onSubmit={handleSubmit(onSubmit)}>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          Task Name
          <span className="node-drawer__required"> *</span>
        </label>
        <input
          type="text"
          className={`node-drawer__input ${errors.name ? 'node-drawer__input--error' : ''}`}
          placeholder="Task name..."
          {...register('name', { required: 'Name is required' })}
        />
        {errors.name && <span className="node-drawer__error">{errors.name.message}</span>}
      </div>

      {isPm && (
        <div className="node-drawer__field">
          <label className="node-drawer__label">Priority</label>
          <select className="node-drawer__input" {...register('priority')}>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="node-drawer__field">
        <label className="node-drawer__label">Status</label>
        <select className="node-drawer__input" {...register('status')}>
          <option value="0">Open</option>
          <option value="1">In Progress</option>
          <option value="2">Completed</option>
        </select>
      </div>

      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {isPm ? 'Procedure / Instructions' : 'Description'}
        </label>
        <textarea
          className="node-drawer__textarea"
          rows={3}
          placeholder={isPm ? 'Procedure details...' : 'Description...'}
          {...register('description')}
        />
      </div>

      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          Save Changes
        </Button>
      </div>
    </form>
  );
};

const DuplicateForm: React.FC<{
  intl: any;
  node: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, node, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { name: `${node?.name || ''} (Copy)` },
  });

  return (
    <form className="node-drawer__form" onSubmit={handleSubmit(onSubmit)}>
      <p className="node-drawer__description">
        {intl.formatMessage(
          { id: 'assets.drawer.duplicateDescription' },
          { nodeName: node?.name || '' }
        )}
      </p>

      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.duplicateName' })}
          <span className="node-drawer__required"> *</span>
        </label>
        <input
          type="text"
          className={`node-drawer__input ${errors.name ? 'node-drawer__input--error' : ''}`}
          placeholder={intl.formatMessage(
            { id: 'assets.drawer.duplicateNamePlaceholder' },
            { nodeName: node?.name || '' }
          )}
          {...register('name', {
            required: intl.formatMessage({ id: 'assets.drawer.duplicateNameRequired' }),
          })}
        />
        {errors.name && <span className="node-drawer__error">{errors.name.message}</span>}
      </div>

      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          {intl.formatMessage({ id: 'assets.drawer.duplicateAction' })}
        </Button>
      </div>
    </form>
  );
};

const MoveForm: React.FC<{
  intl: any;
  node: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, node, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { destination: '' },
  });

  return (
    <form className="node-drawer__form" onSubmit={handleSubmit(onSubmit)}>
      <p className="node-drawer__description">
        {intl.formatMessage(
          { id: 'assets.drawer.moveDescription' },
          { nodeName: node?.name || '' }
        )}
      </p>

      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.moveDestination' })}
          <span className="node-drawer__required"> *</span>
        </label>
        <select
          className={`node-drawer__select ${
            errors.destination ? 'node-drawer__select--error' : ''
          }`}
          {...register('destination', {
            required: intl.formatMessage({ id: 'assets.drawer.moveDestinationRequired' }),
          })}
        >
          <option value="">
            {intl.formatMessage({ id: 'assets.drawer.moveDestinationPlaceholder' })}
          </option>
        </select>
        {errors.destination && (
          <span className="node-drawer__error">{errors.destination.message}</span>
        )}
      </div>

      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          {intl.formatMessage({ id: 'assets.drawer.moveAction' })}
        </Button>
      </div>
    </form>
  );
};

const ToggleRow: React.FC<{
  label: string;
  name: string;
  register: any;
  defaultChecked?: boolean;
}> = ({ label, name, register, defaultChecked = false }) => (
  <label className="node-drawer__toggle-row">
    <input
      type="checkbox"
      className="node-drawer__toggle-input"
      defaultChecked={defaultChecked}
      {...register(name)}
    />
    <span className="node-drawer__toggle-switch" />
    <span className="node-drawer__toggle-label">{label}</span>
  </label>
);

const GroupForm: React.FC<{
  intl: any;
  editData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, editData = null, onSubmit, onCancel }) => {
  const isEdit = Boolean(editData);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: editData?.name || '',
      description: editData?.notes || editData?.description || '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="node-drawer__form">
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.groupName' })}
        </label>
        <input
          className={`node-drawer__input ${errors.name ? 'node-drawer__input--error' : ''}`}
          placeholder={intl.formatMessage({ id: 'assets.drawer.groupNamePlaceholder' })}
          {...register('name', { required: true })}
        />
        {errors.name && (
          <span className="node-drawer__error">
            {intl.formatMessage({ id: 'assets.drawer.groupNameRequired' })}
          </span>
        )}
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.groupDescription' })}
        </label>
        <textarea
          className="node-drawer__textarea"
          rows={3}
          placeholder={intl.formatMessage({ id: 'assets.drawer.groupDescriptionPlaceholder' })}
          {...register('description')}
        />
      </div>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          {isEdit
            ? intl.formatMessage({ id: 'assets.drawer.saveGroup' })
            : intl.formatMessage({ id: 'assets.drawer.addGroup' })}
        </Button>
      </div>
    </form>
  );
};

const AssetForm: React.FC<{
  intl: any;
  groups: any[];
  editData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, groups, editData = null, onSubmit, onCancel }) => {
  const isEdit = Boolean(editData);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: editData?.name || '',
      assetGroupId: editData?.assetGroupId != null ? String(editData.assetGroupId) : '',
      notes: editData?.notes || '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="node-drawer__form">
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.assetName' })}
        </label>
        <input
          className={`node-drawer__input ${errors.name ? 'node-drawer__input--error' : ''}`}
          placeholder={intl.formatMessage({ id: 'assets.drawer.assetNamePlaceholder' })}
          {...register('name', { required: true })}
        />
        {errors.name && (
          <span className="node-drawer__error">
            {intl.formatMessage({ id: 'assets.drawer.assetNameRequired' })}
          </span>
        )}
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.assetGroup' })}
        </label>
        <select className="node-drawer__input" {...register('assetGroupId')}>
          <option value="">
            {intl.formatMessage({ id: 'assets.drawer.assetGroupPlaceholder' })}
          </option>
          {(groups || []).map((g: any) => (
            <option key={g.id} value={g.id}>
              {getGroupDisplayName(g)}
            </option>
          ))}
        </select>
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.drawer.assetNotes' })}
        </label>
        <textarea
          className="node-drawer__textarea"
          rows={3}
          placeholder={intl.formatMessage({ id: 'assets.drawer.assetNotesPlaceholder' })}
          {...register('notes')}
        />
      </div>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          {isEdit
            ? intl.formatMessage({ id: 'assets.drawer.saveAsset' })
            : intl.formatMessage({ id: 'assets.drawer.addAsset' })}
        </Button>
      </div>
    </form>
  );
};

const templateTypeOptions = Object.values(TemplateType).map((value) => ({
  value,
  label: TemplateTypeLabel[value],
}));

const TemplateForm: React.FC<{
  intl: any;
  editData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}> = ({ intl, editData = null, onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: editData?.name || '',
      description: editData?.description || '',
      type: editData?.type || '',
      version: editData?.version || '',
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="node-drawer__form">
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.template.name' })}
        </label>
        <input
          className={`node-drawer__input ${errors.name ? 'node-drawer__input--error' : ''}`}
          placeholder={intl.formatMessage({ id: 'assets.template.namePlaceholder' })}
          {...register('name', { required: true })}
        />
        {errors.name && (
          <span className="node-drawer__error">
            {intl.formatMessage({ id: 'assets.template.nameRequired' })}
          </span>
        )}
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.template.description' })}
        </label>
        <textarea
          className="node-drawer__textarea"
          rows={3}
          placeholder={intl.formatMessage({ id: 'assets.template.descriptionPlaceholder' })}
          {...register('description')}
        />
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.template.type' })}
        </label>
        <select
          className={`node-drawer__input ${errors.type ? 'node-drawer__input--error' : ''}`}
          {...register('type', { required: true })}
        >
          <option value="">{intl.formatMessage({ id: 'assets.template.typePlaceholder' })}</option>
          {templateTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {errors.type && (
          <span className="node-drawer__error">
            {intl.formatMessage({ id: 'assets.template.typeRequired' })}
          </span>
        )}
      </div>
      <div className="node-drawer__field">
        <label className="node-drawer__label">
          {intl.formatMessage({ id: 'assets.template.version' })}
        </label>
        <input
          className="node-drawer__input"
          placeholder={intl.formatMessage({ id: 'assets.template.versionPlaceholder' })}
          {...register('version')}
        />
      </div>
      <div className="node-drawer__actions">
        <Button type="button" color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: 'assets.actions.cancel' })}
        </Button>
        <Button type="submit" color="primary">
          {intl.formatMessage({ id: 'assets.template.saveTemplate' })}
        </Button>
      </div>
    </form>
  );
};

const AddToNodeDrawer: React.FC<AddToNodeDrawerProps> = ({
  isOpen,
  mode,
  node,
  breadcrumb,
  groups = [],
  taskOccurrences = [],
  inventoryItems = [],
  isAssetContext: isAssetContextProp = false,
  initialItemType = null,
  editData = null,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const [selectedItemType, setSelectedItemType] = useState<AddItemType>(
    initialItemType || 'dailyTask'
  );
  const isAssetContext =
    isAssetContextProp ||
    node?.type === 'group' ||
    node?.type === 'item' ||
    node?.type === 'template';
  const isItemContext = node?.type === 'item';
  const isTemplateContext = node?.type === 'template';
  const [inventorySubMode, setInventorySubMode] = useState<'link' | 'create'>('link');
  const [taskSubMode, setTaskSubMode] = useState<'link' | 'create'>('link');

  React.useEffect(() => {
    if (initialItemType) {
      setSelectedItemType(initialItemType);
    }
  }, [initialItemType]);

  React.useEffect(() => {
    setTaskSubMode('link');
  }, [selectedItemType, mode]);

  const filteredTaskOccurrences = useMemo(() => {
    const bucketByCard: Record<AddItemType, string | null> = {
      dailyTask: TASK_BUCKET.DailyTask,
      prevMaint: TASK_BUCKET.PrevMaint,
      project: TASK_BUCKET.Project,
      inventory: null,
    };
    const targetBucket = bucketByCard[selectedItemType];
    if (!targetBucket) return taskOccurrences;
    return (taskOccurrences || []).filter(
      (o: any) => getTaskBucket(getOccurrenceType(o)) === targetBucket
    );
  }, [taskOccurrences, selectedItemType]);

  const handleClose = () => {
    setSelectedItemType('dailyTask');
    setTaskSubMode('link');
    onClose();
  };

  const handleFormSubmit = (data: any) => {
    if (mode) {
      const itemType = mode === 'add' ? selectedItemType : null;
      onSubmit(mode, itemType, data);
    }
    setSelectedItemType('dailyTask');
    setTaskSubMode('link');
  };

  const pathItems = (breadcrumb || []).slice(1, -1);
  const breadcrumbText = pathItems.map((b: any) => b.name).join(' \u203A ');

  const nodeName = node?.name || '';

  const renderTitle = () => {
    const values = { nodeName };
    switch (mode) {
      case 'duplicate':
        return intl.formatMessage({ id: 'assets.drawer.duplicate' }, values);
      case 'move':
        return intl.formatMessage({ id: 'assets.drawer.move' }, values);
      case 'addGroup':
        return intl.formatMessage({ id: 'assets.drawer.newGroup' }, values);
      case 'addAsset':
        return intl.formatMessage({ id: 'assets.drawer.newAsset' }, values);
      case 'editTask':
        return intl.formatMessage({ id: 'assets.drawer.editTask' }, values);
      case 'editGroup':
        return intl.formatMessage({ id: 'assets.drawer.editGroup' }, values);
      case 'editAsset':
        return intl.formatMessage({ id: 'assets.drawer.editAsset' }, values);
      case 'editTemplate':
        return intl.formatMessage({ id: 'assets.drawer.editTemplate' }, values);
      default:
        return intl.formatMessage({ id: 'assets.drawer.addTo' }, values);
    }
  };

  const renderTypeLabel = (key: AddItemType) => {
    switch (key) {
      case 'dailyTask':
        return intl.formatMessage({ id: 'assets.drawer.type.dailyTask' });
      case 'prevMaint':
        return intl.formatMessage({ id: 'assets.drawer.type.prevMaint' });
      case 'project':
        return intl.formatMessage({ id: 'assets.drawer.type.project' });
      case 'inventory':
      default:
        return intl.formatMessage({ id: 'assets.drawer.type.inventory' });
    }
  };

  return (
    <Drawer
      expanded={isOpen && !!node}
      overlay
      expandedWidth={NODE_DRAWER_WIDTH}
      onBackdropClick={handleClose}
    >
      {node && (
        <div className="node-drawer">
          <div className="node-drawer__header">
            <div className="node-drawer__header-text">
              <h3 className="node-drawer__title">{renderTitle()}</h3>
              {breadcrumbText && <span className="node-drawer__breadcrumb">{breadcrumbText}</span>}
            </div>
            <DrawerControls closeFunction={handleClose} />
          </div>

          <div className="node-drawer__body">
            {mode === 'add' && (
              <>
                <div className="node-drawer__type-section">
                  <span className="node-drawer__type-label">
                    {intl.formatMessage({ id: 'assets.drawer.whatAdding' })}
                  </span>
                  <div className="node-drawer__type-cards">
                    {typeCards.map((card) => (
                      <button
                        key={card.key}
                        type="button"
                        className={`node-drawer__type-card ${
                          selectedItemType === card.key
                            ? `node-drawer__type-card--selected node-drawer__type-card--${card.colorClass}`
                            : ''
                        }`}
                        onClick={() => setSelectedItemType(card.key)}
                      >
                        <span
                          className={`node-drawer__type-icon node-drawer__type-icon--${card.colorClass}`}
                        >
                          <i className={`uil ${card.icon}`} />
                        </span>
                        <span className="node-drawer__type-text">{renderTypeLabel(card.key)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {isAssetContext && selectedItemType !== 'inventory' && (
                  <div className="node-drawer__submode">
                    <span className="node-drawer__submode-label">
                      {intl.formatMessage({ id: 'assets.drawer.taskSource' })}
                    </span>
                    <div className="node-drawer__submode-toggle">
                      <button
                        type="button"
                        className={`node-drawer__submode-btn ${
                          taskSubMode === 'link' ? 'node-drawer__submode-btn--active' : ''
                        }`}
                        onClick={() => setTaskSubMode('link')}
                      >
                        {intl.formatMessage({ id: 'assets.drawer.taskSource.link' })}
                      </button>
                      <button
                        type="button"
                        className={`node-drawer__submode-btn ${
                          taskSubMode === 'create' ? 'node-drawer__submode-btn--active' : ''
                        }`}
                        onClick={() => setTaskSubMode('create')}
                      >
                        {intl.formatMessage({ id: 'assets.drawer.taskSource.create' })}
                      </button>
                    </div>
                  </div>
                )}

                {isAssetContext && selectedItemType !== 'inventory' && taskSubMode === 'link' && (
                  <LinkExistingTaskForm
                    intl={intl}
                    nodeName={node?.name || ''}
                    occurrences={filteredTaskOccurrences}
                    onSubmit={handleFormSubmit}
                    onCancel={handleClose}
                  />
                )}

                {isAssetContext && taskSubMode === 'create' && selectedItemType !== 'inventory' && (
                  <CreateNewTaskCta
                    intl={intl}
                    nodeName={nodeName}
                    onSubmit={handleFormSubmit}
                    onCancel={handleClose}
                  />
                )}

                {selectedItemType === 'inventory' && (isItemContext || isTemplateContext) && (
                  <div className="node-drawer__submode">
                    <span className="node-drawer__submode-label">
                      {intl.formatMessage({ id: 'assets.drawer.inventorySource' })}
                    </span>
                    <div className="node-drawer__submode-toggle">
                      <button
                        type="button"
                        className={`node-drawer__submode-btn ${
                          inventorySubMode === 'link' ? 'node-drawer__submode-btn--active' : ''
                        }`}
                        onClick={() => setInventorySubMode('link')}
                      >
                        {intl.formatMessage({ id: 'assets.drawer.inventorySource.link' })}
                      </button>
                      <button
                        type="button"
                        className={`node-drawer__submode-btn ${
                          inventorySubMode === 'create' ? 'node-drawer__submode-btn--active' : ''
                        }`}
                        onClick={() => setInventorySubMode('create')}
                      >
                        {intl.formatMessage({ id: 'assets.drawer.inventorySource.create' })}
                      </button>
                    </div>
                  </div>
                )}

                {selectedItemType === 'inventory' &&
                  (isItemContext || isTemplateContext) &&
                  inventorySubMode === 'link' && (
                    <LinkExistingInventoryForm
                      intl={intl}
                      nodeName={node?.name || ''}
                      inventories={inventoryItems}
                      onSubmit={handleFormSubmit}
                      onCancel={handleClose}
                    />
                  )}

                {selectedItemType === 'inventory' &&
                  (isItemContext || isTemplateContext) &&
                  inventorySubMode === 'create' && (
                    <CreateNewInventoryCta
                      intl={intl}
                      nodeName={nodeName}
                      onSubmit={handleFormSubmit}
                      onCancel={handleClose}
                    />
                  )}
                {selectedItemType === 'inventory' && !isItemContext && !isTemplateContext && (
                  <div className="node-drawer__form">
                    <p className="node-drawer__hint">
                      {intl.formatMessage({
                        id: 'assets.drawer.linkInventory.itemOnlyHint',
                      })}
                    </p>
                    <div className="node-drawer__actions">
                      <Button type="button" color="secondary" outline onClick={handleClose}>
                        {intl.formatMessage({ id: 'assets.actions.cancel' })}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {mode === 'duplicate' && (
              <DuplicateForm
                intl={intl}
                node={node}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'move' && (
              <MoveForm
                intl={intl}
                node={node}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'addGroup' && (
              <GroupForm intl={intl} onSubmit={handleFormSubmit} onCancel={handleClose} />
            )}

            {mode === 'editGroup' && (
              <GroupForm
                intl={intl}
                editData={editData}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'addAsset' && (
              <AssetForm
                intl={intl}
                groups={groups}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'editAsset' && (
              <AssetForm
                intl={intl}
                groups={groups}
                editData={editData}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'editTask' && (
              <EditTaskForm
                intl={intl}
                editData={editData}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}

            {mode === 'editTemplate' && (
              <TemplateForm
                intl={intl}
                editData={editData}
                onSubmit={handleFormSubmit}
                onCancel={handleClose}
              />
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default AddToNodeDrawer;
